/** Feature modules that superadmin can enable/disable platform-wide. */
export const MODULE_KEYS = [
  "ecommerce",
  "raffles",
  "auctions",
  "packs",
  "credits",
  "collection",
  "vault",
  "trading",
  "ai_chat_fe",
  "ai_copilot_bo",
  "support_forms",
  "card_uploader",
  "referrals",
  "documents",
  "invoicing",
  "cms",
  "instalments",
  "marketplace",
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

export const MODULE_LABELS: Record<ModuleKey, string> = {
  ecommerce: "Ecommerce",
  raffles: "Raffles",
  auctions: "Auctions",
  packs: "Pack Opening",
  credits: "Credit Token",
  collection: "Collection (Storefront)",
  vault: "Vault",
  instalments: "FlexxiPlan",
  trading: "P2P Trading",
  ai_chat_fe: "AI Chat (Storefront)",
  ai_copilot_bo: "AI Copilot (Backoffice)",
  support_forms: "Dynamic Forms",
  card_uploader: "Card Uploader",
  referrals: "Referrals",
  documents: "Payment receipts & documents",
  invoicing: "Tax invoicing",
  cms: "Storefront CMS",
  marketplace: "C2C Marketplace",
};

/** Modules enabled by default on a fresh install. */
export const MODULE_DEFAULTS: Record<ModuleKey, boolean> = {
  ecommerce: true,
  raffles: true,
  auctions: true,
  packs: true,
  credits: true,
  collection: true,
  vault: false,
  trading: false,
  ai_chat_fe: true,
  ai_copilot_bo: true,
  support_forms: true,
  card_uploader: true,
  referrals: false,
  documents: true,
  invoicing: false,
  cms: true,
  instalments: false,
  marketplace: false,
};

export function moduleLabel(key: ModuleKey): string {
  return MODULE_LABELS[key];
}

/** Document types available for the current module flags. */
export function availableDocumentTypes(
  modules: Pick<Record<ModuleKey, boolean>, "documents" | "invoicing">,
): ("INVOICE" | "RECEIPT")[] {
  const types: ("INVOICE" | "RECEIPT")[] = [];
  if (modules.documents) types.push("RECEIPT");
  if (modules.invoicing) types.push("INVOICE");
  return types;
}

export function isDocumentTypeModuleEnabled(
  type: "INVOICE" | "RECEIPT",
  modules: Pick<Record<ModuleKey, boolean>, "documents" | "invoicing">,
): boolean {
  return type === "RECEIPT" ? modules.documents : modules.invoicing;
}

/** Storefront routes gated by feature module. */
export const MODULE_STOREFRONT_ROUTES: Partial<Record<ModuleKey, string[]>> = {
  ecommerce: ["/shop", "/cart", "/checkout"],
  packs: ["/packs", "/leaderboard"],
  raffles: ["/raffles"],
  auctions: ["/auctions"],
  credits: ["/wallet"],
  collection: ["/inventory"],
  instalments: ["/inventory", "/flexxi"],
  vault: ["/inventory"],
  support_forms: ["/support"],
};

/** Backoffice routes gated by feature module. */
export const MODULE_BACKOFFICE_ROUTES: Partial<Record<ModuleKey, string[]>> = {
  ecommerce: ["/shop-listings"],
  raffles: ["/raffles"],
  auctions: ["/auctions"],
  packs: ["/packs"],
  card_uploader: ["/card-uploader"],
  cms: ["/content/pages", "/content/slider"],
  documents: ["/admin/document-templates"],
  instalments: ["/flexxi/templates", "/flexxi/plans"],
  ai_copilot_bo: ["/admin/ai/models"],
  support_forms: ["/admin/support/forms", "/cases"],
};
